import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AddOn from './AddOnTemplate';
import { buttonLabels } from '../../../Constants/Data';
import { retrieveProducts } from '../../API/API_Handler';
import charmhealthlogo_placeholder from '../../../assets/charmhealthlogo_placeholder.jpg';
import { ExpandCategoriesColumnIcon, CollapseCategoriesColumnIcon, MagnifyingGlassIcon } from '../../../Constants/Icons';
import { useUser } from '../../../UserContext';
import ListingLoader from './ListingLoader';

const AddOns = ({ page }) => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const navigate = useNavigate();
    const { publicProducts } = useUser();
    const [categoriesExpanded, setCategoriesExpanded] = useState(true);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [data, setData] = useState(null);
    const [filteredServices, setFilteredServices] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [isSuggestionsDropdownOpen, setIsSuggestionsDropdownOpen] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const suggestionsDropdownRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');


    const handleSidebarClick = () => {
        setCategoriesExpanded(!categoriesExpanded);
    };

    useEffect(() => {
        if (page !== "all") {
            const matchingButton = buttonLabels.find(button => button.urlLabel === page);
            if (matchingButton) {
                handleCategoryChange(matchingButton);
            }
        }
        else {
            handleCategoryChange({ label: "All", urlLabel: "all", number: null })
            _retrieveProducts(null)
        }
    }, [page]);

    useEffect(() => {
        if (page === "all") {
            setData(publicProducts);
            setFilteredServices(publicProducts);
        }
    }, [publicProducts]);

    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        const urlFriendlyLabel = createUrlFriendlyLabel(category.label);
        navigate(`/explore/category/${urlFriendlyLabel}`, { replace: true });
        if (category.number !== null) {
            _retrieveProducts(category.number)
        }
        else {
            _retrieveProducts(null)
        }
    }

    const createUrlFriendlyLabel = (label) => {
        return encodeURIComponent(label.toLowerCase().replace(/\s+/g, '-'));
    };

    const _retrieveProducts = async (categoryID) => {
        setLoading(true);
        try {
            if (categoryID === null) {
                setData(publicProducts);
                setFilteredServices(publicProducts);
                setLoadingProducts(false);
                return;
            }
            const products = await retrieveProducts(categoryID)
            setData(products);
            setFilteredServices(products);
            setLoadingProducts(false);

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const extractCategoryIds = (product) => {
        if (Array.isArray(product.categories)) {
            return product.categories.map(category => category.id);
        }
        return [];
    };

    const categoryDropdownRef = useRef(null);

    const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);

    const toggleCategoryDropdown = () => {
        setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
        setIsSuggestionsDropdownOpen(false);
    };

    const handleCategorySelect = (category) => {
        handleCategoryChange(category);
        setIsCategoryDropdownOpen(false);
    };

    const handleButtonClick = (productID) => {
        setIsSuggestionsDropdownOpen(false);
        navigate(`/explore/${productID}`);
    };

    const handleChange = (event) => {
        const value = event.target.value;
        setSearchTerm(value);

        if (value) {
            const filteredSuggestions = filteredServices.filter(item =>
                (item['name'] && item['name'].toLowerCase().includes(value.toLowerCase())) ||
                (item['description'] && item['description'].toLowerCase().includes(value.toLowerCase()))
            );

            const sortedSuggestions = filteredSuggestions.sort((a, b) => {
                const aIncludes = a['name'] && a['name'].toLowerCase().includes(value.toLowerCase());
                const bIncludes = b['name'] && b['name'].toLowerCase().includes(value.toLowerCase());
                return (aIncludes === bIncludes) ? 0 : aIncludes ? -1 : 1;
            });

            setSuggestions(sortedSuggestions);
            setIsSuggestionsDropdownOpen(true);
            setIsCategoryDropdownOpen(false);
        } else {
            setIsSuggestionsDropdownOpen(false);
            setSuggestions([]);
            setIsCategoryDropdownOpen(false);
        }
    };

    const styles = {
        AddOnsContainer: "min-h-screen w-full flex flex-row",
        CategoriesSidebarContainer: "hidden md:block",
        CategoriesSidebarExpandButton: "mt-2 text-white rounded-r-full min-w-12 min-h-10 max-h-10 flex items-center justify-center bg-exploreCategoriesBackground",
        CategoriesSidebarCollapseButtonStyling: "w-full flex justify-end",
        CategoriesSidebarCollapseButton: "mt-2 text-white rounded-l-full min-w-12 min-h-10 max-h-10 flex items-center justify-center bg-exploreCategoriesBackground",
        CategoriesSidebarExpandedContainer: "w-[428px] h-screen flex-none flex flex-col items-start bg-gradient-to-b from-[#4c0b35] to-[#7e1a5a]",
        CategoriesSidebarTitle: "mt-4 mb-2 w-full font-bold text-center text-xl md:text-2xl 2xl:text-3xl text-exploreCategoriesText",
        ContentContainer: "flex flex-col flex-grow h-screen overflow-y-auto",
        SuggestionsDropdownContainer: "absolute top-[48px] md:top-[68px] left-0 w-fit pt-2 md:pt-4 px-2 md:px-4 bg-heroSearchbar border-2 border-exploreSearchBorder max-h-80 md:max-h-[40rem] overflow-y-auto",
        SuggestionsDropdownList: "list-none p-0 m-0",
        SuggestionsDropdownListItem: "shadow-lg m-0 p-0 gap-4 ",
        SuggestionsDropdownListItemButton: "bg-productBackground mb-2 md:mb-4 grid grid-cols-10 h-20 md:h-24 items-center w-full",
        LogoContainer: "col-span-1 h-16 w-16 md:h-20 md:w-20",
        DetailsContainer: "ml-2 md:ml-4 col-span-9 flex flex-col",
        SuggestionName: "text-left text-base md:text-lg",
        SuggestionDescription: "text-left line-clamp-2 text-sm md:text-xl 2xl:text-2xl"
    }

    return (
        <div className="w-full flex flex-row bg-[#f0f0f0]">
            {categoriesExpanded ? (
                // Categories sidebar expanded
                <div className="hidden md:block min-w-[320px] h-screen overflow-y-auto bg-gradient-to-b from-[#28051E] to-[#882B67]">
                    <div className="w-full h-fit mt-[46px] flex items-center relative">
                        <h3 className="w-full text-center font-medium text-xl md:text-3xl text-exploreCategoriesText">Categories</h3>
                        <button className="w-[48px] h-[36px] bg-[#640444] absolute right-0 top-0 rounded-l-full flex items-center justify-center" onClick={() => handleSidebarClick()}>
                            <svg className="mr-1" width="12" height="12" viewBox="0 0 12 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0.883618 8.59285C0.883618 8.36756 0.987368 8.14232 1.20966 7.9645L10.1018 0.85064C10.5316 0.506818 11.243 0.506818 11.6728 0.85064C12.1026 1.19446 12.1026 1.76358 11.6728 2.10742L3.56609 8.59277L11.6728 15.0781C12.1026 15.422 12.1026 15.9911 11.6728 16.3349C11.243 16.6787 10.5316 16.6787 10.1018 16.3349L1.20966 9.22105C0.987373 9.04322 0.883618 8.81792 0.883618 8.59269V8.59285Z" fill="white" />
                            </svg>
                        </button>
                    </div>
                    <div className="mt-[20px] w-full flex flex-col">
                        {buttonLabels.map((item, index) => (
                            <button
                                key={index}
                                className={`font-proximanova text-white text-[19px] py-[12px] ${selectedCategory.label === item.label ? 'bg-exploreCategoriesBackground  font-[600]' : 'bg-clear font-[400]'}`}
                                onClick={() => {
                                    handleCategoryChange(item);
                                }}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>
            ) :
                // Categories sidebar collapsed
                <button className="hidden mt-4 w-[60px] min-w-[60px] h-[49px] mt-[46px] bg-[#640444] rounded-r-full md:flex items-center justify-center" onClick={() => handleSidebarClick()}>
                    <svg className="mr-1" width="22" height="22" viewBox="0 0 12 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.9953 8.59269C11.9953 8.81799 11.8915 9.04322 11.6693 9.22105L2.77709 16.3349C2.34732 16.6787 1.63592 16.6787 1.20612 16.3349C0.776345 15.9911 0.776345 15.422 1.20612 15.0781L9.31282 8.59277L1.20612 2.10742C0.776345 1.7636 0.776345 1.19448 1.20612 0.85064C1.6359 0.506818 2.3473 0.506818 2.77709 0.85064L11.6693 7.9645C11.8915 8.14233 11.9953 8.36762 11.9953 8.59285V8.59269Z" fill="white" />
                    </svg>
                </button>
            }

            {/* Main content */}
            <div className="h-screen overflow-y-auto flex flex-grow flex-col px-[32px] 1.5xl:px-[48px] bg-[#f0f0f0] pb-12">
                <div className="w-full 3xl:w-[1800px] md:max-h-[56px] xl:max-h-[66px] mt-[20px] xl:mt-[43px] xl:mb-[43px] px-[28px] gap-[16px] flex flex-row items-center bg-white border-exploreSearchBorder border-1 rounded-full relative">
                    <div ref={categoryDropdownRef} className="w-fit flex items-center">
                        <button
                            className="w-fit gap-[16px] pr-[16px] flex flex-row items-center border-r-[1px] border-exploreSearchBorder font-medium"
                            onClick={toggleCategoryDropdown}>
                            <span className="truncate">{selectedCategory.label}</span>
                            {isCategoryDropdownOpen ? (
                                <svg className="" width="14" height="12" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9.16026 0.5L17.8205 15.5H0.5L9.16026 0.5Z" fill="#9A0169" />
                                </svg>
                            ) : (
                                <svg className="" width="14" height="12" viewBox="0 0 18 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8.66026 15L0 0L17.3205 0L8.66026 15Z" fill="#9A0169" />
                                </svg>
                            )}
                        </button>
                        {isCategoryDropdownOpen && (
                            <div className="absolute top-[48px] md:top-[68px] left-0 w-fit sm:pr-4 md:pr-8 px-2 md:px-4 bg-heroSearchbar shadow-lg border-2 border-exploreSearchBorder max-h-64 md:max-h-80 overflow-y-auto">
                                <ul className="py-1">
                                    {buttonLabels.map((label, index) => (
                                        <li key={index}>
                                            <button
                                                className="w-full text-left font-proximanova text-[18px] font-[600] text-[#505050] px-2 md:px-4 py-1 md:py-2 hover:bg-exploreNavBackgroundSelected hover:text-white focus:outline-none"
                                                onClick={() => handleCategorySelect(label)}>
                                                {label.label}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {isSuggestionsDropdownOpen && suggestions.length > 0 && (
                            <div className="absolute top-[48px] md:top-[68px] left-0 w-full pt-2 md:pt-4 px-2 md:px-4 bg-heroSearchbar border-2 border-exploreSearchBorder max-h-80 md:max-h-[40rem] overflow-y-auto" ref={suggestionsDropdownRef}>
                                <ul className="list-none p-0 m-0">
                                    {suggestions.map((suggestion, index) => (
                                        <li key={index} className="h-[76px] bg-white shadow-lg p-[6px] mb-2">
                                            <button
                                                className="w-full flex flex-row items-center gap-[16px]" onClick={() => handleButtonClick(suggestion['id'])}
                                            >
                                                <div className="min-h-[64px] min-w-[64px] max-h-[64px] max-w-[64px] flex items-center justify-center">
                                                    <img
                                                        src={suggestion['thumbnail']
                                                            ? suggestion['thumbnail']
                                                            : charmhealthlogo_placeholder}
                                                        alt="Logo"
                                                        className="h-full w-full max-h-[64px] max-w-[64px] object-contain"
                                                    />
                                                </div>


                                                <div className="flex flex-col">
                                                    <p className="font-proximanova font-[700] text-[18px] text-[#505050] text-left line-clamp-2 mb-0" style={{ lineHeight: '1.20' }}>{suggestion.name}</p>
                                                    <p className="font-proximanova text-[18px] text-[#505050] text-left line-clamp-2 mb-0" style={{ lineHeight: '1.20' }}>{suggestion.description}</p>
                                                </div>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                    <div className="ml-4 md:ml-6 flex-grow flex justify-center">
                        <input
                            type="text"
                            className="w-full h-10 focus:outline-none font-proximanova text-[18px] text-[#505050] mb-0" placeholder="Search for apps"
                            value={searchTerm}
                            onChange={handleChange}
                        />
                        <button className="">
                            <MagnifyingGlassIcon />
                        </button>
                    </div>
                </div>
                {
                    loadingProducts ? (
                        <div className="mt-[20px] xl:mt-[0px] grid grid-cols-1 xl:grid-cols-2 1.5xl:grid-cols-3 3xl:grid-cols-4 gap-[22px]">
                            <ListingLoader />
                            <ListingLoader />
                            <ListingLoader />
                            <ListingLoader />
                            <ListingLoader />
                        </div>
                    ) : (
                        <div className="mt-[20px] xl:mt-[0px] grid grid-cols-1 xl:grid-cols-2 1.5xl:grid-cols-3 3xl:grid-cols-4 gap-[22px]">
                            {Object.values(data).map((item, index) => (
                                <AddOn
                                    key={item.id}
                                    id={item.id}
                                    thumbnail={item.thumbnail}
                                    name={item.name}
                                    description={item.description}
                                    vendor_id={item.vendor_id}
                                    vendor_name={item.vendor_name}
                                    categories={extractCategoryIds(item)}
                                    expanded={categoriesExpanded}
                                />

                            ))}
                        </div>
                    )
                }
            </div>

        </div>
    );
}
export default AddOns;